import { NotificationBell } from '@/components/NotificationBell';
import { AccountMenu } from '@/components/AccountMenu';
import { CircleHelp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useHousehold } from '@/contexts/HouseholdContext';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
  SidebarIcon,
  type SidebarIconName,
} from '@/components/icons/SidebarIcon';
import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface SidebarProps {
  className?: string;
  hideCollapseToggle?: boolean;
}

interface NavItem {
  name: string;
  href: string;
  icon: SidebarIconName;
}

const collapsedLabelClass =
  'pointer-events-none absolute left-full top-1/2 z-[60] ml-2 -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-sm font-medium text-popover-foreground opacity-0 shadow-md transition-opacity duration-150 group-hover/sidebar-item:opacity-100 group-focus-visible/sidebar-item:opacity-100';

export function Sidebar({
  className,
  hideCollapseToggle = false,
}: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const inSettings =
    location.pathname === '/preferences' || location.pathname === '/settings';
  const { logout } = useAuth();
  const { isInHousehold } = useHousehold();

  const navigationItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      { name: 'Dashboard', href: '/dashboard', icon: 'dashboard' },
      { name: 'Accounts', href: '/accounts', icon: 'accounts' },
      { name: 'Budget', href: '/budget', icon: 'budget' },
    ];
    if (isInHousehold) {
      items.push({ name: 'Household', href: '/household', icon: 'household' });
    }
    items.push({
      name: 'Transactions',
      href: '/transactions',
      icon: 'transactions',
    });
    items.push({ name: 'Assistant', href: '/assistant', icon: 'assistant' });
    return items;
  }, [isInHousehold]);

  const pathOf = (href: string): string => href.split('?')[0] ?? href;

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Logout failures are surfaced by auth state remaining unchanged.
    }
  };

  return (
    <div
      className={cn(
        'relative z-50 flex h-dvh shrink-0 flex-col overflow-visible border-r bg-card',
        isCollapsed ? 'w-16' : 'w-64',
        className
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'relative flex h-16 items-center border-b border-border px-3',
          isCollapsed ? 'justify-center' : 'justify-between'
        )}
      >
        {isCollapsed && !hideCollapseToggle ? (
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Expand sidebar"
            className="group/sidebar-item relative flex items-center justify-center rounded-xl p-2 transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-border/60 bg-background shadow-sm">
              <img
                src="/richtato.png"
                alt=""
                className="h-7 w-7 rounded-lg transition-opacity group-hover/sidebar-item:opacity-0 group-focus-visible/sidebar-item:opacity-0"
              />
              <SidebarIcon
                name="expand"
                className="absolute h-5 w-5 text-muted-foreground opacity-0 transition-opacity group-hover/sidebar-item:opacity-100 group-focus-visible/sidebar-item:opacity-100"
              />
            </div>
            <span className={collapsedLabelClass}>Expand sidebar</span>
          </button>
        ) : (
          <Link
            to="/dashboard"
            className={cn(
              'group/sidebar-item flex min-w-0 items-center rounded-xl transition-colors hover:bg-muted/70',
              isCollapsed
                ? 'relative justify-center p-2'
                : 'gap-3 py-2 pl-2 pr-3'
            )}
            aria-label="Go to dashboard"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-background shadow-sm">
              <img
                src="/richtato.png"
                alt="Richtato Logo"
                className="h-7 w-7 rounded-lg"
              />
            </div>
            {!isCollapsed && (
              <span className="truncate font-serif text-2xl tracking-tight text-foreground">
                Richtato
              </span>
            )}
            {isCollapsed && (
              <span className={collapsedLabelClass}>Richtato</span>
            )}
          </Link>
        )}
        {!hideCollapseToggle && !isCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-0"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
          >
            <SidebarIcon
              name="collapse"
              size={16}
              className="h-4 w-4 opacity-70"
            />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-3" role="navigation">
        {navigationItems.map(item => {
          const itemPath = pathOf(item.href);
          const isActive =
            location.pathname === itemPath ||
            location.pathname.startsWith(`${itemPath}/`);

          return (
            <Link
              key={item.name}
              to={item.href}
              data-tour={item.name === 'Accounts' ? 'nav-accounts' : undefined}
              aria-label={isCollapsed ? item.name : undefined}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'group/sidebar-item flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200',
                isActive
                  ? 'bg-hero text-primary'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                isCollapsed && 'relative justify-center px-2'
              )}
            >
              <SidebarIcon name={item.icon} className="h-5 w-5 shrink-0" />
              {!isCollapsed && (
                <span className="flex-1 flex items-center justify-between">
                  <span>{item.name}</span>
                </span>
              )}
              {isCollapsed && (
                <span className={collapsedLabelClass}>{item.name}</span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="px-3 pb-3">
        <Link
          to="/help"
          aria-label="Help"
          aria-current={location.pathname === '/help' ? 'page' : undefined}
          className={cn(
            'group/sidebar-item relative flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            location.pathname === '/help' && 'bg-hero text-primary',
            isCollapsed && 'justify-center px-2'
          )}
        >
          <CircleHelp className="h-5 w-5 shrink-0" aria-hidden="true" />
          {isCollapsed ? (
            <span className={collapsedLabelClass}>Help</span>
          ) : (
            <span>Help</span>
          )}
        </Link>
        <Link
          to="/preferences"
          aria-label="Settings"
          aria-current={inSettings ? 'page' : undefined}
          className={cn(
            'group/sidebar-item relative flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            inSettings
              ? 'bg-hero text-primary'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            isCollapsed && 'justify-center px-2'
          )}
        >
          <SidebarIcon name="settings" className="h-5 w-5 shrink-0" />
          {isCollapsed ? (
            <span className={collapsedLabelClass}>Settings</span>
          ) : (
            <span>Settings</span>
          )}
        </Link>
      </div>

      <div
        className={cn(
          'flex items-center gap-1 border-t border-border p-3',
          isCollapsed && 'flex-col gap-2'
        )}
      >
        <AccountMenu collapsed={isCollapsed} />
        <NotificationBell
          compact
          animated
          side={isCollapsed ? 'right' : 'top'}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => void handleLogout()}
          aria-label="Log out"
          title="Log out"
          className="group/sidebar-item relative h-9 w-9 shrink-0 rounded-xl text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        >
          <SidebarIcon name="logout" size={18} className="h-[18px] w-[18px]" />
          {isCollapsed && <span className={collapsedLabelClass}>Log out</span>}
        </Button>
      </div>
    </div>
  );
}
